import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import axios from "axios";
import { parse, formatISO } from "date-fns"; // Import date-fns for date handling
import { useNotification } from "../../../../context/NotificationContext";

const MoveRoomPopup = ({ bookingId, onSubmit, bookingData, isOpen, onClose }) => {
  const [formValues, setFormValues] = useState({});
  const [availableRooms, setAvailableRooms] = useState({});
  const [assignedRooms, setAssignedRooms] = useState([]);
  const [rateInfo, setRateInfo] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);
  const submitButtonRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const { notify } = useNotification();

  useEffect(() => {
    if (isOpen && bookingId) {
      fetchAssignedRooms();
      fetchAvailableRooms();
    }
  }, [isOpen, bookingId]);

  // Utility function to convert date string to UTC ISO format
// Utility function to convert date string to UTC ISO format
const parseDateToUTC = (dateString) => {
  try {
    console.log("Parsing date:", dateString); // Debug: Log the input date string

    // Check if the dateString is in ISO format (e.g., "2025-02-21T00:00:00Z")
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;
    if (isoDateRegex.test(dateString)) {
      const parsedDate = new Date(dateString);
      if (isNaN(parsedDate.getTime())) {
        throw new Error("Invalid ISO date format");
      }
      // Convert to UTC ISO string (e.g., "2025-02-21T00:00:00Z")
      return formatISO(parsedDate, { representation: "complete" });
    }

    // Fallback: Try parsing as "MMM d, yyyy" format (e.g., "Feb 19, 2025")
    const parsedDate = parse(dateString, "MMM d, yyyy", new Date());
    if (isNaN(parsedDate.getTime())) {
      throw new Error("Invalid date format, expected 'MMM d, yyyy'");
    }

    // Convert to UTC ISO string (e.g., "2025-02-19T00:00:00Z")
    return formatISO(parsedDate, { representation: "complete" });
  } catch (error) {
    console.error("Error parsing date:", error.message, "Input:", dateString);
    return null;
  }
};

  const initializeForm = (rooms) => {
    const initialValues = {};
    rooms.forEach((room) => {
      initialValues[room.room_number] = {
        newRoom: null,
        charges: parseFloat(room.room_rate) || 0,
        overwriteCharges: false,
      };
    });
    setFormValues(initialValues);
  };

  const fetchAssignedRooms = async () => {
    try {
      const response = await axios.get(`${apiUrl}/booking/distinct-assigned-rooms/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setAssignedRooms(response.data.assignedRooms);
        initializeForm(response.data.assignedRooms);
      }
    } catch (error) {
      notify("error","Failed to fetch assigned rooms.");
      console.error("Error fetching assigned rooms:", error);
    }
  };

  const fetchAvailableRooms = async () => {
    try {
      const roomTypeRes = await axios.get(`${apiUrl}/booking/room-types/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const roomTypeIds = roomTypeRes.data.roomTypeIds;
      // Convert check-in and check-out dates to UTC ISO format
      const checkInUTC = parseDateToUTC(bookingData.checkIn);
      const checkOutUTC = parseDateToUTC(bookingData.checkOut);

      // Validate the converted dates
      if (!checkInUTC || !checkOutUTC) {
        notify("error","Invalid check-in or check-out date format.");
        return;
      }

      const response = await axios.post(
        `${apiUrl}/booking/roomno-availability`,
        {
          check_in: checkInUTC,
          check_out: checkOutUTC,
          room_type_ids: roomTypeIds,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const roomsByType = response.data.availableRooms.reduce((acc, room) => {
        if (!acc[room.room_type_name]) acc[room.room_type_name] = [];
        acc[room.room_type_name].push({
          value: room.room_number,
          label: room.room_number,
          rate: room.base_rate_single,
        });
        return acc;
      }, {});

      setAvailableRooms(roomsByType);
    } catch (error) {
      notify("error","Failed to fetch available rooms.");
      setAvailableRooms({});
    }
  };

  const handleChange = (roomNumber, field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [roomNumber]: {
        ...prev[roomNumber],
        [field]: field === "charges" ? Number(value) : value,
      },
    }));
    setErrors((prev) => ({ ...prev, [roomNumber]: { ...prev[roomNumber], [field]: "" } }));

    if (field === "newRoom" && value) {
      const roomType = assignedRooms.find((r) => r.room_number === roomNumber)?.room_type;
      const selectedRoom = availableRooms[roomType]?.find((r) => r.value === value.value);
      if (selectedRoom && selectedRoom.rate !== formValues[roomNumber].charges) {
        setRateInfo((prev) => ({
          ...prev,
          [roomNumber]: {
            ...prev[roomNumber],
            newRate: selectedRoom.rate,
            rateFetched: true,
          },
        }));
      } else {
        setRateInfo((prev) => ({
          ...prev,
          [roomNumber]: {
            ...prev[roomNumber],
            newRate: null,
            rateFetched: false,
          },
        }));
      }
    }
  };

  const validateForm = () => {
    let validationErrors = {};
    assignedRooms.forEach((room) => {
      const roomKey = room.room_number;
      if (formValues[roomKey]?.newRoom && formValues[roomKey].newRoom.value === room.room_number) {
        validationErrors[roomKey] = { newRoom: "Cannot select the same room." };
      }
      if (formValues[roomKey]?.overwriteCharges && !formValues[roomKey]?.charges) {
        validationErrors[roomKey] = { ...validationErrors[roomKey], charges: "Charges required when overwriting" };
      } else if (formValues[roomKey]?.charges && (isNaN(formValues[roomKey].charges) || formValues[roomKey].charges <= 0)) {
        validationErrors[roomKey] = { ...validationErrors[roomKey], charges: "Charges must be positive" };
      }
    });
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      notify("error","Please fix validation errors");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        bookingId: bookingId,
        roomUpdates: assignedRooms.map((room) => {
          const roomData = formValues[room.room_number];
          return {
            currentRoomNumber: room.room_number,
            newRoomNumber: roomData?.newRoom?.value,
            price: roomData?.overwriteCharges
              ? roomData.charges
              : roomData?.applyNewRate && rateInfo[room.room_number]?.newRate !== null
              ? rateInfo[room.room_number].newRate
              : parseFloat(room.room_rate), // Include current room_rate if not overwritten
          };
        }),
      };

      const response = await axios.put(`${apiUrl}/booking/move-room-sametype`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify("success","Rooms moved successfully!");
      if (onSubmit) onSubmit(response.data);
      onClose();

      if (submitButtonRef.current) {
        submitButtonRef.current.click();
      }
    } catch (error) {
      notify("error","Failed to move rooms.");
      if (error.response?.data?.message === "Room unavailable") {
        notify("error","Selected room unavailable. Please choose another.");
        fetchAvailableRooms();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  const isCheckedOutOrCancelledOrNoShow = ["checked-out", "cancelled", "noshow"].includes(
    bookingData?.bookingStatus
  );
  const disableFields = isCheckedOutOrCancelledOrNoShow;

  return (
    <div ref={modalRef} id="move_room" className="modal custom-modal fade" tabIndex="-1" role="dialog">
      <div
        className="modal-dialog modal-lg"
        style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "40%" }}
        role="document"
      >
        <form onSubmit={handleSubmit} style={{ height: "100%" }}>
          <div className="modal-content h-100">
            <div className="modal-header" style={{ backgroundColor: '#f0f8ff', borderBottom: '1px solid #e0e7ff' }}>
              <h5 className="modal-title" style={{ fontWeight: '600', color: '#333' }}>Move Room</h5>
              <button type="button" className="btn-close" onClick={handleCancel} />
            </div>

            <div className="modal-body" style={{ maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
              <div className="row g-2">
                <div className="col-12 mb-2">
                  <div className="card" style={{ border: '1px solid #e0e7ff', padding: '10px' }}>
                    <h6 className="card-title mb-2">Guest Details</h6>
                    <div className="row g-0">
                      <div className="col-6 pe-2">
                        <p><strong>Folio No:</strong> {bookingData?.bookingNumber || "N/A"}</p>
                        <p><strong>Arrival:</strong> {new Date(bookingData?.checkIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || "N/A"}</p>
                        <p><strong>Departure:</strong> {new Date(bookingData?.checkOut).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || "N/A"}</p>
                      </div>
                      <div className="col-6 ps-2">
                        <p><strong>Status:</strong> {bookingData?.bookingStatus || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="card" style={{ border: '1px solid #e0e7ff', padding: '10px' }}>
                    <h6 className="card-title mb-2">Move Rooms</h6>
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr style={{ backgroundColor: '#e0e7ff' }}>
                            <th>Assigned Category</th>
                            <th>Current Room</th>
                            <th>New Room No.</th>
                            <th>Charges</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignedRooms.map((room, index) => {
                            const availableOptions = (availableRooms[room.room_type] || []).filter(
                              (option) => option.value !== room.room_number
                            );
                            return (
                              <tr key={index}>
                                <td>{room.room_type}</td>
                                <td>{room.room_number}</td>
                                <td>
                                  <Select
                                    value={formValues[room.room_number]?.newRoom || null}
                                    onChange={(option) => handleChange(room.room_number, "newRoom", option)}
                                    options={availableOptions}
                                    isClearable
                                    placeholder={availableOptions.length > 0 ? "Select Room" : "No Room Available"}
                                    isDisabled={disableFields || availableOptions.length === 0}
                                    styles={{
                                      control: (provided) => ({
                                        ...provided,
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        backgroundColor:
                                          disableFields || availableOptions.length === 0 ? '#f8f9fa' : 'white',
                                      }),
                                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                    }}
                                  />
                                  {errors[room.room_number]?.newRoom && (
                                    <div className="invalid-feedback d-block" style={{ color: '#dc3545' }}>
                                      {errors[room.room_number].newRoom}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={formValues[room.room_number]?.charges || 0}
                                    onChange={(e) => handleChange(room.room_number, "charges", e.target.value)}
                                    disabled={!(formValues[room.room_number]?.overwriteCharges || formValues[room.room_number]?.newRoom)}
                                    min="0"
                                    step="0.01"
                                    style={{
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      border: '1px solid #e0e0e0',
                                      backgroundColor: !(formValues[room.room_number]?.overwriteCharges || formValues[room.room_number]?.newRoom) ? '#f8f9fa' : 'white',
                                      width: '100px',
                                    }}
                                  />
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                    Current Rate: {parseFloat(room.room_rate).toFixed(2)}
                                  </div>
                                  <div className="d-flex align-items-center mt-1">
                                    <label className="switch" style={{ marginRight: '8px' }}>
                                      <input
                                        type="checkbox"
                                        checked={formValues[room.room_number]?.overwriteCharges || false}
                                        onChange={(e) => handleChange(room.room_number, "overwriteCharges", e.target.checked)}
                                        disabled={disableFields}
                                      />
                                      <span className="slider round"></span>
                                    </label>
                                    <span>Overwrite Charges</span>
                                  </div>
                                  {errors[room.room_number]?.charges && (
                                    <div className="invalid-feedback d-block" style={{ color: '#dc3545' }}>
                                      {errors[room.room_number].charges}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ backgroundColor: '#f0f8ff' }}>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading || disableFields}>
                Save
              </button>

              <div style={{ display: "none" }}>
                <button ref={submitButtonRef} data-bs-dismiss="modal" aria-label="Close" type="reset">
                  Submit2
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MoveRoomPopup;